import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from '@/utils/lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAppContext } from '@/context/AppContext';
import { findPageByPath, followUpExists } from '@/utils/pagePaths';
import { trackImageOpened } from '@/services/analytics';
import { getParentId } from '@/utils/pagePaths';
import { SiteLogoMark } from '@components/shared/FilteredMark';
import { compactMeta, shareImageMeta, shareSiteTitle, resolveMediaShareCard } from '@/share/meta';
import { mediaLook } from '@/blocks/look';
import { previewLibrary } from '@/preview/hoverChrome';
import useChromeLayer from '@/hooks/useChromeLayer';
import { pageViewerLook, themeViewerLook, viewerLookVars } from '@/theme/viewer-look';
import { ViewerLookContext } from './components/MediaFrame';

// Local imports
import {
    FADE_DURATION,
    ENABLE_VIEWER_PAGE_FADES,
    toUrl,
    parseImageParams,
    prepareSlidesData,
    prepareDesktopSlidesData,
    flattenImages,
    calculateUnifiedPageSize,
} from './imageViewerUtils';

import {
    useSlideshowExperience,
    useSwipeToClose,
    useSwipeNavigation,
    useKeyboardNavigation,
    useImagePreload,
    useOnboarding,
    useFadeTransition,
} from './hooks';

import {
    OnboardingOverlay,
    ImageSlide,
    PrevLink,
    NextLink,
    CloseButton,
    EditorialLogo,
    TouchSwiper,
    ImageNotFound,
} from './components';

/**
 * Image Viewer Component
 * Displays a full-screen single image with navigation (masonry / non-slideshow pages).
 * For slideshow (editorial) pages use SpreadViewer instead.
 *
 * @param {Object} props.match - React Router match object
 * @param {Object} props.history - React Router history object
 * @param {Object} props.location - React Router location object
 */
function ImageViewer({ match: matchProp, history: historyProp, location: locationProp }) {
    const params = useParams();
    const locationFromRouter = useLocation();
    const navigate = useNavigate();
    const location = locationProp ?? locationFromRouter;
    const match = matchProp ?? { params };
    const history = historyProp ?? {
        push: (url, state) => navigate(url, state ? { state } : undefined),
        replace: (url) => navigate(url, { replace: true }),
    };
    // Slide URLs are router paths; inside a subsite the address bar also has
    // the /~slug prefix, so compare with the router instead.
    const routerPathRef = useRef(locationFromRouter.pathname);
    routerPathRef.current = locationFromRouter.pathname;

    const { content, urlBase, theme } = useAppContext();
    const chromeLayer = useChromeLayer();
    const experience = useSlideshowExperience();
    const screenShape = experience.shape;

    const parsed = parseImageParams(match, location);
    const imageId = parsed.imageId;
    const slug = parsed.slug;
    const page = slug;
    const pagesObj = content?.pages || {};
    
    const isTouchDevice = experience.touch;
    
    // Hydration state - must start false to match SSR output
    const [isHydrated, setIsHydrated] = useState(false);
    // Entrance fade-in state (skipped while ENABLE_VIEWER_PAGE_FADES is false)
    const [isEntered, setIsEntered] = useState(!ENABLE_VIEWER_PAGE_FADES);
    // Guard to prevent multiple close navigations
    const [isClosing, setIsClosing] = useState(false);
    
    // Refs
    const swiperRef = useRef(null);
    const figureRef = useRef(null);
    const swipeWrapperRef = useRef(null);
    const editorialWrapperRef = useRef(null); // Full-screen touch target for iPad editorial
    const imageContentRef = useRef(null); // For swipe transform (images only, not UI)
    
    // Fade transition for navigation
    const { fadeOut, fadeOpacity, navigateWithFade } = useFadeTransition(history);
    
    // Close and navigate immediately - RootLayout handles the fade-out transition
    const closeWithFade = useCallback((url, shouldFocus = false, { dismissed = false } = {}) => {
        if (isClosing) return;
        setIsClosing(true);
        // Store imageId in sessionStorage if we should focus after closing
        if (shouldFocus && imageId && typeof window !== 'undefined') {
            sessionStorage.setItem('focusImageId', imageId);
        }
        // Navigate immediately - RootLayout keeps this component in an overlay and fades it out.
        // A swipe already threw the picture away, so RootLayout covers with plain background instead.
        history.push(url, dismissed ? { viewerDismissed: true } : undefined);
    }, [history, isClosing, imageId]);
    
    // Find page by full path if slug exists
    let pageDataObj = null;
    if (page) {
        pageDataObj = findPageByPath(page, pagesObj);
        // Fallback to direct lookup for backward compatibility
        if (!pageDataObj) {
            pageDataObj = pagesObj[page] || null;
        }
    }
    
    // ImageViewer is for masonry only; slideshow pages use SpreadViewer
    const isEditorial = false;

    let closeUrl = page ? `/${page}` : '/';
    
    // Content data
    const properties = content?.properties || {};
    const pageData = pageDataObj || {};
    const name = pageData.name || '';
    const spreadGap = pageData.gallery?.spreadGap || pageData.spreadGap || 0;
    const exists = !page || !!pageDataObj;
    const rawImages = page && pageDataObj ? (pageDataObj.gallery?.images || pageDataObj.images || []) : _.result(content, 'home.gallery.images', _.result(content, 'home.images', []));
    // Flatten images for index/navigation calculations (spreads become individual images)
    const flatImages = useMemo(() => flattenImages(rawImages), [rawImages]);
    const files = content?.files || {};
    const pagesForHtml = content?.pages || {};
    // Use flat images for index calculation
    const index = imageId ? flatImages.indexOf(imageId) : -1;
    const pageAdvanceUrl = page ? _.result(content, ['pages', page, 'advanceUrl'], null) : null;
    const advanceUrl = followUpExists(pageAdvanceUrl, pagesObj) ? pageAdvanceUrl : null;
    const isFirst = index === 0;
    const isLast = index === flatImages.length - 1;
    
    // For backward compatibility, keep images reference for places that expect flat array
    const images = flatImages;
    
    // Desktop slides with spreads preserved (for desktop rendering)
    const desktopSlidesData = useMemo(() => {
        return prepareDesktopSlidesData(rawImages, content?.files, page, pagesObj);
    }, [rawImages, content?.files, page, pagesObj]);
    
    // Calculate unified page dimensions from all images
    const pageDimensions = useMemo(() => {
        return calculateUnifiedPageSize(desktopSlidesData);
    }, [desktopSlidesData]);
    
    // Find current slide index and data in desktop slides (may be a spread)
    const desktopSlideIndex = useMemo(() => {
        if (!imageId || !desktopSlidesData.length) return -1;
        return desktopSlidesData.findIndex(slide => {
            if (slide.type === 'spread') {
                return slide.images.some(img => img.id === imageId);
            }
            return slide.id === imageId;
        });
    }, [imageId, desktopSlidesData]);
    
    const currentSlideData = desktopSlideIndex >= 0 ? desktopSlidesData[desktopSlideIndex] : null;
    
    // Check if current view is a spread
    const isCurrentSpread = currentSlideData?.type === 'spread';
    
    // For spreads on desktop, calculate the range of indices (for counter display)
    // e.g., if spread contains images at positions 0 and 1, show "1 - 2 / total"
    const spreadIndexRange = useMemo(() => {
        if (!isCurrentSpread || !currentSlideData?.images || screenShape === 'small') {
            return null;
        }
        const leftId = currentSlideData.images[0]?.id;
        const rightId = currentSlideData.images[1]?.id;
        const leftIndex = flatImages.indexOf(leftId);
        const rightIndex = flatImages.indexOf(rightId);
        if (leftIndex >= 0 && rightIndex >= 0) {
            return {
                start: Math.min(leftIndex, rightIndex) + 1, // 1-indexed
                end: Math.max(leftIndex, rightIndex) + 1,   // 1-indexed
            };
        }
        return null;
    }, [isCurrentSpread, currentSlideData, flatImages, screenShape]);
    
    // Desktop navigation - uses slide-based navigation (spreads are single units)
    const isFirstDesktopSlide = desktopSlideIndex === 0;
    const isLastDesktopSlide = desktopSlideIndex === desktopSlidesData.length - 1;
    
    // Get next/prev desktop slides
    const prevDesktopSlide = desktopSlideIndex > 0 ? desktopSlidesData[desktopSlideIndex - 1] : null;
    const nextDesktopSlide = desktopSlideIndex >= 0 && desktopSlideIndex < desktopSlidesData.length - 1 
        ? desktopSlidesData[desktopSlideIndex + 1] : null;
    
    // Get the representative image ID for a slide (for URL generation)
    const getSlideImageId = (slide) => {
        if (!slide) return null;
        if (slide.type === 'spread') {
            return slide.images[0]?.id; // Use left image ID for spread URLs
        }
        return slide.id;
    };

    // Image data (for non-spread display and metadata)
    const imageData = imageId && exists ? _.result(content, ['files', imageId], null) : null;
    
    // Get parent page info for analytics context (grandparent for nested pages)
    const grandparentName = useMemo(() => {
        if (!pageDataObj) return null;
        const parentId = getParentId(pageDataObj);
        if (!parentId) return null;
        const parentPage = pagesObj[parentId];
        if (!parentPage) return null;
        // Check if parent has a grandparent
        const grandparentId = getParentId(parentPage);
        if (grandparentId && pagesObj[grandparentId]) {
            return pagesObj[grandparentId].name;
        }
        return parentPage.name; // Return parent name as context
    }, [pageDataObj, pagesObj]);
    
    // Track image opened for analytics with full context
    useEffect(() => {
        if (imageId) {
            trackImageOpened({
                imageName: imageId,
                imageDescription: imageData?.description,
                originalName: imageData?.originalname,
                pageName: name || 'Home', // Use "Home" if no page name (home page images)
                pageDescription: pageDataObj?.description,
                grandparentName,
                imageIndex: index >= 0 ? index + 1 : null,
                totalImages: flatImages.length,
            });
        }
    }, [imageId, name, imageData?.description, imageData?.originalname, pageDataObj?.description, grandparentName, index, flatImages.length]);
    
    // Get prev/next slides from desktop slides data (for preloading)
    // These include scale/anchor settings for proper rendering
    const prevSlideData = desktopSlideIndex > 0 ? desktopSlidesData[desktopSlideIndex - 1] : null;
    const nextSlideData = desktopSlideIndex >= 0 && desktopSlideIndex < desktopSlidesData.length - 1 
        ? desktopSlidesData[desktopSlideIndex + 1] 
        : null;
    
    const preloadableSlide = (slide) => (
        slide && slide.type !== 'text' && slide.type !== 'video' ? slide : null
    );
    const prev = preloadableSlide(prevSlideData);
    const next = preloadableSlide(nextSlideData);
    
    // Screen size detection for touch devices
    const isSmallScreen = screenShape === 'small';
    // Phone vs tablet: phones have a short screen side under 500px.
    const isPhone = experience.phone;
    const isLandscape = experience.landscape;
    
    // Slides data for Swiper - single images only (no spreads, no text slide)
    const slidesData = useMemo(() => {
        return prepareSlidesData(rawImages, content?.files, page, pagesObj);
    }, [rawImages, content?.files, page, pagesObj]);
    
    const [currentImageId, setCurrentImageId] = useState(imageId || null);
    const prevImageIdRef = useRef(imageId);

    useEffect(() => {
        if (imageId && prevImageIdRef.current !== imageId) {
            setCurrentImageId(imageId);
        }
        prevImageIdRef.current = imageId;
    }, [imageId]);
    
    const handleSlideChange = useCallback((swiper) => {
        const newIndex = swiper.activeIndex;
        if (newIndex < 0 || newIndex >= slidesData.length) return;
        const newSlide = slidesData[newIndex];
        if (newSlide?.type === 'image' && newSlide.id) {
            setCurrentImageId(newSlide.id);
            if (routerPathRef.current !== newSlide.url) {
                routerPathRef.current = newSlide.url;
                history.replace(newSlide.url);
            }
        }
    }, [slidesData, history]);
    
    const getSlideIndexForImage = useCallback((imgId, slides) => {
        if (!imgId || !slides?.length) return null;
        const idx = slides.findIndex((s) => s.type === 'image' && s.id === imgId);
        return idx >= 0 ? idx : null;
    }, []);
    
    // Navigation URLs - screen-aware (must be computed before hooks)
    // On desktop with spreads, navigate between slides (spreads are single units)
    // On mobile, navigate between individual images
    const isDesktop = screenShape !== 'small';
    
    // Mobile navigation (uses flat images)
    const mobileSlideIndex = slidesData.findIndex((slide) => slide.id === (currentImageId || imageId));
    const mobilePrev = mobileSlideIndex > 0 ? slidesData[mobileSlideIndex - 1] : null;
    const mobileNext = mobileSlideIndex >= 0 && mobileSlideIndex < slidesData.length - 1
        ? slidesData[mobileSlideIndex + 1]
        : null;
    const mobileUrlPrev = isFirst && page
        ? `/${page}`
        : mobilePrev?.url || null;
    const mobileUrlNext = isLast && advanceUrl
        ? advanceUrl
        : mobileNext?.url || null;

    // Desktop navigation (uses desktop slides - spreads are single units)
    const prevDesktopImageId = getSlideImageId(prevDesktopSlide);
    const nextDesktopImageId = getSlideImageId(nextDesktopSlide);

    const desktopUrlPrev = isFirstDesktopSlide && page
        ? `/${page}`
        : prevDesktopSlide?.url || (prevDesktopImageId ? toUrl(page, prevDesktopImageId, pagesObj) : null);

    const desktopUrlNext = isLastDesktopSlide && advanceUrl
        ? advanceUrl
        : nextDesktopSlide?.url || (nextDesktopImageId ? toUrl(page, nextDesktopImageId, pagesObj) : null);
    
    // Use appropriate navigation based on screen size
    const urlPrev = isDesktop ? desktopUrlPrev : mobileUrlPrev;
    const urlNext = isDesktop ? desktopUrlNext : mobileUrlNext;
    const navIsFirst = isDesktop ? isFirstDesktopSlide : isFirst;
    const navIsLast = isDesktop ? isLastDesktopSlide : isLast;
    
    const showOnboarding = useOnboarding(isTouchDevice, isEntered);
    // Phone advances one picture at a time. Desktop advances one slide, which
    // may already be a two-page spread. Ahead preload uses that same unit.
    const preloadSlides = useMemo(() => {
        const source = isDesktop ? desktopSlidesData : slidesData;
        return source.filter((slide) => preloadableSlide(slide));
    }, [isDesktop, desktopSlidesData, slidesData]);
    const preloadCurrent = isDesktop
        ? currentSlideData
        : preloadSlides.find((slide) => slide.id === (currentImageId || imageId)) || null;
    useImagePreload(prev, next, preloadSlides, preloadCurrent);
    
    useSwipeToClose({
        enabled: isTouchDevice,
        onClose: () => closeWithFade(closeUrl, false, { dismissed: true }),
        figureRef,
        swipeWrapperRef,
    });

    useSwipeNavigation({
        enabled: false,
        onPrev: () => {
            if (urlPrev && !navIsFirst) {
                // Direct navigation - slide animation handled by hook
                history.push(urlPrev);
            }
        },
        onNext: () => {
            if (urlNext) {
                if (navIsLast && advanceUrl) {
                    closeWithFade(urlNext);
                } else {
                    // Direct navigation - slide animation handled by hook
                    history.push(urlNext);
                }
            } else if (navIsLast) {
                // On last slide with no advance URL, close the viewer
                closeWithFade(closeUrl);
            }
        },
        targetRef: editorialWrapperRef,
        transformRef: imageContentRef,
        closeTransformRef: editorialWrapperRef, // Use full page for close animation
        slideOut: true,
        canGoPrev: !navIsFirst,
        canGoNext: !navIsLast || !!advanceUrl,
    });
    
    useKeyboardNavigation({
        imageData,
        currentSlideData,
        spreadGap,
        urlPrev,
        urlNext,
        navIsFirst,
        navIsLast,
        isEditorial,
        advanceUrl,
        closeUrl,
        navigateWithFade,
        closeWithFade,
    });
    
    // Debug logging
    if (typeof window !== 'undefined' && !imageData) {
        console.warn('ImageViewer: imageData is null', {
            imageId,
            index,
            images: images.slice(0, 5),
            exists,
            page: page || 'home',
            hasFiles: !!content?.files,
            fileKeys: content?.files ? Object.keys(content.files).slice(0, 5) : 'no files',
            matchParams: match?.params,
        });
    }

    // Fade in on mount and mark as hydrated
    useEffect(() => {
        setIsHydrated(true);
        if (!ENABLE_VIEWER_PAGE_FADES) {
            setIsEntered(true);
            return undefined;
        }
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setIsEntered(true);
            });
        });
        return undefined;
    }, []);

    // Error state
    if (!imageData) {
        return (
            <ImageNotFound 
                imageId={imageId}
                index={index}
                page={page}
                images={images}
                matchParams={match?.params}
            />
        );
    }
    
    // Computed values
    const siteTitle = shareSiteTitle(content, theme);
    const defaultAlt = imageData.description 
        ? `${imageData.description}, ${siteTitle}` 
        : `${name}, ${siteTitle}`;

    // The block this image came from may set its own caption and colours.
    const look = mediaLook(page ? pageDataObj : content?.home, imageId, previewLibrary(content), theme);
    // The page's own viewer colours beat the block's, which beat the theme's.
    const pageLook = pageViewerLook(page ? pageDataObj : content?.home, pagesObj);

    // Common styles
    const containerStyle = {
        opacity: isEntered ? 1 : 0,
        transition: ENABLE_VIEWER_PAGE_FADES ? `opacity ${FADE_DURATION}ms ease-in-out` : 'none',
        ...viewerLookVars(themeViewerLook(theme, chromeLayer)),
        ...look.vars,
        ...viewerLookVars(pageLook),
    };
    
    const { title: ogTitle, description: ogDescription } = resolveMediaShareCard(imageData, {
        content,
        theme,
        page: pageDataObj,
        pages: pagesObj,
        isHome: !page,
    });
    const helmetMeta = compactMeta([
        { property: 'og:title', content: ogTitle },
        { property: 'og:description', content: ogDescription },
        ...shareImageMeta(imageData, urlBase),
        { property: 'og:type', content: 'article' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: ogTitle },
        { name: 'twitter:description', content: ogDescription },
        { name: 'description', content: ogDescription },
    ]);

    // Handle navigation click events
    const handlePrevClick = (e) => {
        e.preventDefault();
        if (navIsFirst) {
            closeWithFade(urlPrev);
        } else {
            navigateWithFade(urlPrev, 'prev', imageData, currentSlideData, spreadGap);
        }
    };

    const handleNextClick = (e) => {
        e.preventDefault();
        if (navIsLast && advanceUrl) {
            closeWithFade(urlNext);
        } else {
            navigateWithFade(urlNext, 'next', imageData, currentSlideData, spreadGap);
        }
    };

    const handleCloseClick = (e) => {
        e.preventDefault();
        closeWithFade(closeUrl);
    };

    return (
        <figure 
            ref={figureRef}
            className="fixed inset-0 w-full h-full z-[9999] bg-background flex flex-col"
            role="img"
            aria-label={defaultAlt}
            style={containerStyle}
            data-viewer-root=""
        >
            <ViewerLookContext.Provider value={pageLook}>
            <Helmet title={siteTitle} meta={helmetMeta} />
            {look.fontFaceCss ? <style>{look.fontFaceCss}</style> : null}
            
            {/* SSR fallback: Simple image without spread complexity */}
            {!isHydrated && imageData && (
                <ImageSlide 
                    imageData={imageData}
                    alt={defaultAlt}
                    priority
                    description={imageData?.description}
                    imageId={imageId}
                    showDescription
                    frame
                />
            )}
            
            {/* Touch devices: Swiper (client-only) */}
            {isHydrated && isTouchDevice && slidesData.length > 0 && (
                <TouchSwiper 
                    ref={swiperRef}
                    slidesData={slidesData}
                    initialSlide={(() => {
                        if (currentImageId) {
                            const found = getSlideIndexForImage(currentImageId, slidesData);
                            if (found !== null) return found;
                        }
                        return index >= 0 ? index : 0;
                    })()}
                    onSlideChange={handleSlideChange}
                    defaultAlt={defaultAlt}
                    swipeWrapperRef={swipeWrapperRef}
                    closeAnimationRef={figureRef}
                    pages={pagesForHtml}
                    files={files}
                    history={history}
                    pageDimensions={pageDimensions}
                    spreadGap={spreadGap}
                    isLandscape={isLandscape}
                    siteTitle={siteTitle}
                    isEditorial={false}
                    onCloseOnLastSlide={() => closeWithFade(closeUrl)}
                    currentSlideIndex={mobileSlideIndex}
                    flatImages={flatImages}
                />
            )}
            
            {/* Phone: Permanent logo in top left (both orientations) - aligned with X button */}
            {isHydrated && isPhone && (
                <div
                    className="fixed top-5 left-5 z-[10000] h-[22px] w-[100px]"
                    style={{ opacity: 0.8 }}
                >
                    <SiteLogoMark logo={theme?.brand?.logo} context="media" />
                </div>
            )}
            
            {/* Larger screens (non-editorial): Logo in top left corner */}
            {isHydrated && !isSmallScreen && !isEditorial && (
                <EditorialLogo 
                    siteTitle={siteTitle}
                    closeUrl={closeUrl}
                    onClose={closeWithFade}
                />
            )}
            
            {/* Desktop: Image layers (client-only) */}
            {isHydrated && !isTouchDevice && (
                <>
                    {prev && (
                        <ImageSlide imageData={prev} hidden zIndex={1} pageDimensions={null} gap={spreadGap} />
                    )}
                    
                    {/* Current image */}
                    <ImageSlide
                        imageData={currentSlideData}
                        alt={defaultAlt}
                        priority
                        pageDimensions={null}
                        gap={spreadGap}
                        description={imageData?.description}
                        imageId={imageId}
                        showDescription
                        frame
                    />

                    {next && (
                        <ImageSlide imageData={next} hidden zIndex={1} pageDimensions={null} gap={spreadGap} />
                    )}

                    {fadeOut && (
                        <ImageSlide
                            imageData={fadeOut.imageData}
                            isOverlay
                            opacity={fadeOpacity}
                            zIndex={3}
                            pageDimensions={null}
                            gap={fadeOut.spreadGap}
                            description={fadeOut.imageData?.description}
                            showDescription
                        />
                    )}
                </>
            )}
            
            <CloseButton url={closeUrl} onClick={handleCloseClick} />
            
            {/* Desktop navigation */}
            {urlPrev && !isTouchDevice && !navIsFirst && (
                <PrevLink url={urlPrev} onClick={handlePrevClick} />
            )}
            {urlNext && !isTouchDevice && (
                <NextLink url={urlNext} onClick={handleNextClick} />
            )}
            
            {/* Mobile onboarding */}
            {showOnboarding && isTouchDevice && isHydrated && (
                <OnboardingOverlay />
            )}
            </ViewerLookContext.Provider>
        </figure>
    );
}

ImageViewer.propTypes = {
    match: PropTypes.object,
    history: PropTypes.object,
    location: PropTypes.object,
};

export default ImageViewer;
